import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { ShieldCheck, Settings2, X } from "lucide-react";

const KEY = "hawkam_consent_v1";

function applyConsent(acceptedAnalytics) {
  if (typeof window === "undefined" || !window.posthog) return;
  try {
    if (acceptedAnalytics) {
      window.posthog.opt_in_capturing();
      if (typeof window.posthog.capture === "function") window.posthog.capture("$pageview");
    } else {
      window.posthog.opt_out_capturing();
    }
  } catch (_e) { /* non-blocking */ }
}

function readStoredConsent() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw || !raw.startsWith("{")) { if (raw) localStorage.removeItem(KEY); return null; }
    const p = JSON.parse(raw);
    if (typeof p?.accepted_analytics !== "boolean") return null;
    return p;
  } catch (_e) { return null; }
}

export default function ConsentBanner() {
  const { lang } = useI18n();
  const [visible, setVisible] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  // Necessary is always ON and non-toggleable.
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    const stored = readStoredConsent();
    if (!stored) { setVisible(true); }
    else { applyConsent(!!stored.accepted_analytics); }
  }, []);

  const persist = async (acceptedAnalytics) => {
    const record = { accepted_analytics: acceptedAnalytics, ts: new Date().toISOString() };
    localStorage.setItem(KEY, JSON.stringify(record));
    applyConsent(acceptedAnalytics);
    try {
      await api.post("/consent", {
        consent_type: acceptedAnalytics ? "analytics" : "necessary_only",
        accepted: acceptedAnalytics,
      });
    } catch (_e) { /* non-blocking */ }
    setVisible(false); setPrefsOpen(false);
  };

  if (!visible) return null;

  const L = (ar, en) => (lang === "ar" ? ar : en);

  return (
    <div data-testid="consent-banner"
         className="fixed bottom-4 left-4 right-4 md:left-6 md:right-6 z-50 animate-fade-up">
      <div className="card-elev bg-navy text-cream border-gold/30 p-5 md:p-6">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex items-start gap-3 flex-1">
            <span className="mt-0.5 text-gold"><ShieldCheck size={22} /></span>
            <p className="text-sm leading-relaxed">
              {L("نستخدم ملفات الارتباط الضرورية لتشغيل الموقع وأخرى اختيارية لتحليل الاستخدام. اختر تفضيلاتك.",
                 "We use necessary cookies to run the site and optional ones for analytics. Choose your preferences.")}
              {" · "}
              <Link to="/policies/cookies" className="underline decoration-gold underline-offset-4 hover:text-gold">
                {L("سياسة ملفات الارتباط", "Cookie Policy")}
              </Link>{" · "}
              <Link to="/policies/privacy" className="underline decoration-gold underline-offset-4 hover:text-gold">
                {L("سياسة الخصوصية", "Privacy Policy")}
              </Link>
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button data-testid="consent-reject" onClick={() => persist(false)}
                    className="border border-cream/40 text-cream/90 hover:text-gold hover:border-gold px-4 py-2.5 rounded-sm text-sm">
              {L("رفض", "Reject")}
            </button>
            <button data-testid="consent-prefs" onClick={() => setPrefsOpen(true)}
                    className="border border-cream/40 text-cream/90 hover:text-gold hover:border-gold px-4 py-2.5 rounded-sm text-sm inline-flex items-center gap-1.5">
              <Settings2 size={13} /> {L("حفظ تفضيلاتي", "Save My Preferences")}
            </button>
            <button data-testid="consent-accept" onClick={() => persist(true)}
                    className="bg-gold text-navy font-semibold px-5 py-2.5 rounded-sm hover:-translate-y-0.5 transition-transform text-sm">
              {L("موافقة على الجميع", "Accept All")}
            </button>
          </div>
        </div>

        {prefsOpen && (
          <div className="mt-5 pt-5 border-t border-gold/20" data-testid="consent-prefs-panel">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-gold uppercase tracking-widest">
                {L("تفضيلات الكوكيز", "Cookie Preferences")}
              </h4>
              <button onClick={() => setPrefsOpen(false)} className="text-cream/60 hover:text-gold"><X size={16} /></button>
            </div>
            <div className="grid gap-3">
              <label className="flex items-start gap-3 text-sm">
                <input type="checkbox" checked disabled className="mt-1 accent-gold" />
                <div className="flex-1">
                  <div className="font-medium">{L("الضرورية", "Strictly Necessary")}</div>
                  <div className="text-cream/70 text-xs">
                    {L("مطلوبة لتشغيل الموقع (جلسة الدخول، التفضيلات، الأمان) — لا يمكن تعطيلها.",
                       "Required to run the site (session, preferences, security) — cannot be disabled.")}
                  </div>
                </div>
              </label>
              <label className="flex items-start gap-3 text-sm cursor-pointer" data-testid="consent-analytics-toggle">
                <input type="checkbox" checked={analytics}
                       onChange={(e) => setAnalytics(e.target.checked)}
                       className="mt-1 accent-gold" />
                <div className="flex-1">
                  <div className="font-medium">{L("التحليلات", "Analytics")}</div>
                  <div className="text-cream/70 text-xs">
                    {L("لفهم كيف يستخدم الزوار الموقع (PostHog) — تُفعَّل فقط عند موافقتك الصريحة.",
                       "Understanding how visitors use the site (PostHog) — only enabled with your explicit consent.")}
                  </div>
                </div>
              </label>
            </div>
            <div className="mt-4 flex justify-end">
              <button onClick={() => persist(analytics)}
                      className="bg-gold text-navy font-semibold px-5 py-2 rounded-sm text-sm hover:-translate-y-0.5 transition-transform"
                      data-testid="consent-prefs-save">
                {L("حفظ", "Save")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
